import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewChecked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { LoggerApiService } from '@vendasta/wsp-logger';
import { ChatCompletionResponse, SiteManagerApiService } from '@vendasta/wsp-support-tools';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SiteRouteParams } from '../../core/routeparams';
import { ErrorLogsDataSource } from './error-logs.data-source';

@Component({
  selector: 'app-error-logs',
  templateUrl: './error-logs.component.html',
  styleUrls: ['./error-logs.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ErrorLogsComponent implements OnInit, OnDestroy, AfterViewChecked {
  displayedColumns = ['log'];
  subscriptions: Subscription[] = [];
  dataSource: ErrorLogsDataSource;
  siteId: string;
  noLogsUrl: Observable<string>;
  pageNumber: number;
  public logSummary: string;
  public isAnalyzing = false;
  public analyzingLog: string;
  public logSummaries = [];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('downloadAnchor', { static: true })
  private downloadAnchor: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private snowplow: ProductAnalyticsService,
    private loggerService: LoggerApiService,
    private snackbarService: SnackbarService,
    private siteManagerApiService: SiteManagerApiService,
  ) {
    this.siteId = (this.route.snapshot.params as SiteRouteParams).siteId;
    this.dataSource = new ErrorLogsDataSource(this.siteId, this.loggerService, this.snowplow);
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.updateNoLogsImage();
    this.downloadLogHandler();
    this.setupPaginatorLabels();
  }

  ngAfterViewChecked() {
    this.updatePaginatorLabel();
  }

  private setupPaginatorLabels(): void {
    // Subscribe to page changes to update the custom label
    this.subscriptions.push(
      this.dataSource.pageOptions$.subscribe((val) => {
        this.pageNumber = val.index + 1;
        this.updatePaginatorLabel();
      }),
    );
  }

  private updatePaginatorLabel(): void {
    const list = document.getElementsByClassName('mat-paginator-range-label');
    if (Array.isArray(list) && list.length && list[0] && this.pageNumber) {
      list[0].innerHTML = 'Page : ' + this.pageNumber;
    }
  }
  initSubscriptions(): void {
    this.subscriptions.push(this.translateService.onLangChange.subscribe(() => this.updateNoLogsImage()));
  }

  refreshLogs(): void {
    this.dataSource.refresh();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  toggleStaging(event: MatButtonToggleChange): void {
    this.dataSource.updateLogsType(event.value === 'staging');
    this.refreshLogs();
  }

  private updateNoLogsImage(): void {
    this.noLogsUrl = this.translateService
      .stream('PAGES.PHP_LOGS.NO_LOGS.IMG')
      .pipe(map((url) => `https://vstatic-prod.apigateway.co/wsp-portal-client.9223372035062171829.demo/assets/images/${url}`));
  }

  downloadLogHandler(): void {
    this.dataSource.log$.subscribe(
      (log) => {
        // Based off this article: https://www.illucit.com/en/angular/angular-5-httpclient-file-download-with-authentication/
        const singleLog = log[0];
        const logBlob = new Blob([singleLog.log]);
        const url = window.URL.createObjectURL(logBlob);
        const link = this.downloadAnchor.nativeElement;
        link.href = url;
        link.download = singleLog.timestamp.toString() + '.txt';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      () => {
        this.snackbarService.errorSnack('PAGES.PHP_LOGS.TABLE.DOWNLOAD_LOG_FAILED');
      },
    );
  }

  downloadLog(timestamp: string): void {
    this.dataSource.getLogByTimestamp(timestamp);
  }

  analyzeLogs(id: string, log: string): void {
    if (!this.checkIfAnalyzed(id)) {
      this.analyzingLog = id;
      this.isAnalyzing = true;
      this.siteManagerApiService
        .chatCompletion({ text: 'Please provide details and recommendations if any on the Wordpress log - ' + log })
        .subscribe(
          (data: ChatCompletionResponse) => {
            this.logSummaries.push({ id, summary: data.resp });
            this.isAnalyzing = false;
          },
          () => {
            this.isAnalyzing = false;
          },
        );
    }
  }

  checkIfAnalyzed(id) {
    for (let i = 0; i < this.logSummaries.length; ++i) {
      const summary = this.logSummaries[i];
      if (summary.id == id) {
        return true;
      }
    }
    return false;
  }
}
